import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { LoggerProvider } from '@shared';
import { AppError } from '../../models/app-error.model';
import { ToastProvider } from '../toast/toast.provider';

@Injectable({ providedIn: 'root' })
export class ErrorHandlerProvider {
  private readonly _toast = inject(ToastProvider);
  private readonly _logger = inject(LoggerProvider).createScoped('ErrorHandler');

  private readonly _errorMessages: Record<string, string> = {};

  private readonly _fieldMapping: Record<string, string> = {
    'CaseType.Name.Duplicate': 'name',
    'InvestigationType.Name.Duplicate': 'name',
    'InvestigationType.Category.NotFound': 'categoryId',
  };

  public handle(error: AppError, context?: string): void {
    const message = this.getMessage(error);
    const title = context || 'Error';
    this._toast.error(title, message);

    this._logger.error(`[${error.code}]`, error);
  }

  public applyToForm(
    error: AppError,
    formGroup: FormGroup,
    options?: { showToast?: boolean }
  ): boolean {
    const showToast = options?.showToast ?? false;

    // Get field name from error or mapping
    const fieldName = error.field || this._fieldMapping[error.code];

    if (fieldName) {
      const control = formGroup.get(fieldName);
      if (control) {
        control.setErrors({
          serverError: this.getMessage(error),
        });
        control.markAsTouched();

        // Optionally show toast even when error is applied to field
        if (showToast) {
          this._toast.error('Error', this.getMessage(error));
        }

        return true;
      }
    }

    return false;
  }

  public handleWithForm(
    error: AppError,
    formGroup: FormGroup,
    options?: { context?: string; showToastOnFieldError?: boolean }
  ): void {
    const appliedToField = this.applyToForm(error, formGroup, {
      showToast: options?.showToastOnFieldError ?? false,
    });

    // If not applied to field, show toast
    if (!appliedToField) {
      this.handle(error, options?.context);
    }
  }

  public clearFormErrors(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control?.hasError('serverError')) {
        const errors = { ...control.errors };
        delete errors['serverError'];
        control.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }
    });
  }

  public getMessage(error: AppError): string {
    return this._errorMessages[error.code] || error.message;
  }

  public hasCode(error: AppError, code: string): boolean {
    return error.code === code;
  }

  public addErrorMessage(code: string, message: string): void {
    this._errorMessages[code] = message;
  }

  public addFieldMapping(code: string, fieldName: string): void {
    this._fieldMapping[code] = fieldName;
  }
}
